/**
 * Step 3 — the Hour Optimizer as a door (user, 12/10/2026: "it should open
 * automatically … smooth to open and close"): the card's one line says what
 * it does, its switch turns it on or off, and clicking it opens the Flow
 * Optimizer-style window — Formula · Rules · Config, this campaign's hours
 * live on the right. Used by the container and the master config editors.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { Zap } from 'lucide-react';
import { describeHourFormula, effectiveHourFormula, type DropGenHourOptimizerConfig } from '@trafficpulse/shared';

import { StepCard, Switch } from '@/components/ui';

import { OptimizerDialog } from '../../optimizer-dialog/OptimizerDialog';
import { rulesSummary } from '../../rules/rule-model';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { HourOptimizerContent } from './HourOptimizerContent';

/** The card's one line: on — what it scores on, spacing, extras, rules; off — drops stay as planned. */
export function hourOptimizerSummary(o: DropGenHourOptimizerConfig): string {
  if (!o.enabled) return 'off — drops stay as planned';
  return [`on · ${describeHourFormula(effectiveHourFormula(o))} · up to ${o.maxExtraSlots} extra · ${o.minIntervalMin} min apart`, rulesSummary(o.rules)].filter(Boolean).join(' · ');
}

interface Props {
  index: number;
  editor: ContainerEditor;
  countryKey: string;
  /** Resolved container start (chain-aware) — outlines the eligible hours. */
  startMin: number | null;
  /** The end time on the country's clock (null = none). */
  endMin: number | null;
  /** Planned slot minutes — ticked on the chart. */
  slotMinutes: number[];
  /** The country's window (recipient-local minutes) — drawn on the hour chart. */
  windowStartMin: number;
  windowEndMin: number;
}

export const HourOptimizerStep: FC<Props> = ({ index, editor, windowStartMin, windowEndMin, ...rest }) => {
  const [open, setOpen] = useState(false);
  const config = editor.draft.hourOptimizer;
  const countryWindow = useMemo(() => ({ startMin: windowStartMin, endMin: windowEndMin }), [windowStartMin, windowEndMin]);
  return (
    <>
      <StepCard
        index={index}
        icon={Zap}
        title="Hour Optimizer"
        summary={hourOptimizerSummary(config)}
        onLaunch={() => setOpen(true)}
        headerRight={<Switch checked={config.enabled} onCheckedChange={(enabled) => editor.setHourOptimizer({ enabled })} aria-label="Hour Optimizer enabled" />}
      />
      <OptimizerDialog open={open} onOpenChange={setOpen}>
        <HourOptimizerContent editor={editor} {...rest} window={countryWindow} onClose={() => setOpen(false)} />
      </OptimizerDialog>
    </>
  );
};
